import { Box, Tabs, Tab } from '@mui/material';
import { useState, SyntheticEvent, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nData } from '../../../hooks/api/useI18n';
import {
  getAvailableLocales,
  hasTextItems,
  hasIconItems,
} from '../utils/i18nHelpers';

type TabSelectProps = {
  onTabSelect: (tab: string) => void;
  i18nData?: I18nData;
  publicI18nData?: I18nData;
  currentTab?: string;
};

export const TabSelect = ({
  onTabSelect,
  i18nData,
  publicI18nData,
  currentTab = 'general',
}: TabSelectProps) => {
  const { t } = useTranslation();
  const dataSource = publicI18nData || i18nData;
  const availableLocales = getAvailableLocales(dataSource);
  const [value, setValue] = useState(currentTab);

  // Sync local state with prop changes
  useEffect(() => {
    setValue(currentTab);
  }, [currentTab]);

  const handleTabChange = (event: SyntheticEvent, newValue: string) => {
    setValue(newValue);
    onTabSelect(newValue);
  };

  const languageNames = useMemo(
    () => new Intl.DisplayNames(['en'], { type: 'language' }),
    []
  );

  const getLanguageLabel = (locale: string) =>
    languageNames.of(locale) || locale.toUpperCase();

  // Build tabs array based on available locales and data structure
  const tabs = useMemo(
    () => [
      {
        value: 'general',
        label: t('branding.sections.general'),
        ariaLabel: undefined,
      },
      {
        value: 'display',
        label: t('branding.sections.display'),
        ariaLabel: undefined,
      },
      // Text tabs - only show if locale has string values
      ...availableLocales
        .filter((locale) => hasTextItems(locale, dataSource))
        .map((locale: string) => ({
          value: locale === 'en' ? 'text' : `text-${locale}`,
          label: `${t('branding.sections.text')} (${locale.toUpperCase()})`,
          ariaLabel: `${t('branding.sections.text')} ${getLanguageLabel(locale)}`,
        })),
      // Icons tabs - only show if locale has hash values
      ...availableLocales
        .filter((locale) => hasIconItems(locale, dataSource))
        .map((locale: string) => ({
          value: locale === 'en' ? 'icons' : `icons-${locale}`,
          label: `${t('branding.sections.icons')} (${locale.toUpperCase()})`,
          ariaLabel: `${t('branding.sections.icons')} ${getLanguageLabel(locale)}`,
        })),
    ],
    [availableLocales, dataSource, t]
  );

  return (
    <Box
      sx={{
        marginTop: 6,
        marginBottom: 4,
      }}
    >
      <Tabs
        value={value}
        onChange={handleTabChange}
        selectionFollowsFocus
        aria-label={String(t('branding.tabNavigation', 'Branding tabs'))}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            id={`branding-tab-${index}`}
            role="tab"
            aria-label={tab.ariaLabel}
          />
        ))}
      </Tabs>
    </Box>
  );
};
